import { FormEvent, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import ReactQuill from "react-quill";
import {
  addDoc,
  collection,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../core/firebase";
import { useAuth } from "../core/auth-context";

const stripHtml = (input: string) =>
  input.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();

const SubmitScreen = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [displayName, setDisplayName] = useState(
    user?.displayName ?? ""
  );
  const [tags, setTags] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const plainText = useMemo(() => stripHtml(body), [body]);
  const wordCount = useMemo(
    () =>
      plainText.length === 0
        ? 0
        : plainText.split(/\s+/).filter(Boolean).length,
    [plainText]
  );

  const titleOk = title.trim().length >= 3;
  const bodyOk = plainText.length >= 50;
  const canSubmit = titleOk && bodyOk && !submitting && !!user;

  const parseTags = (raw: string): string[] => {
    return Array.from(
      new Set(
        raw
          .split(/[,\n]+/)
          .map((t) => t.trim().toLowerCase())
          .filter((t) => t.length > 0 && t.length <= 24)
          .slice(0, 8)
      )
    );
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!canSubmit || !user) return;
    setSubmitting(true);
    setError(null);
    try {
      const author =
        displayName.trim() ||
        user.displayName ||
        (user.email ? user.email.split("@")[0] : "anonymous");

      await addDoc(collection(db, "posts"), {
        title: title.trim(),
        author,
        selftext: plainText,
        selftext_html: body,
        tags: parseTags(tags),
        source: "user",
        isApproved: false,
        isHidden: false,
        created: Math.floor(Date.now() / 1000),
        createdAt: serverTimestamp(),
        submittedBy: {
          uid: user.uid,
          email: user.email ?? null,
          displayName: user.displayName ?? null,
        },
      });

      setSubmitted(true);
    } catch (err: any) {
      setError(err?.message ?? "Failed to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="max-w-xl mx-auto py-16 text-center">
        <p className="text-xs uppercase tracking-[0.4em] text-accent-400 mb-2">
          Received
        </p>
        <h1 className="font-display text-4xl text-white">
          Thanks for your story.
        </h1>
        <p className="text-ink-300 mt-4">
          A moderator will review it before it appears in the feed.
          You'll see it on the home page once it's approved.
        </p>
        <div className="mt-8 flex justify-center gap-3">
          <Link
            to="/"
            className="px-4 py-2 rounded-md border border-ink-600 text-ink-200 hover:border-ink-400"
          >
            Back to stories
          </Link>
          <button
            onClick={() => {
              setTitle("");
              setBody("");
              setTags("");
              setSubmitted(false);
            }}
            className="px-4 py-2 rounded-md bg-accent-500 hover:bg-accent-400 text-white"
          >
            Submit another
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      <div className="text-center mb-8">
        <p className="text-xs uppercase tracking-[0.4em] text-accent-400 mb-2">
          Share something unsettling
        </p>
        <h1 className="font-display text-4xl text-white">Submit a story</h1>
        <p className="text-ink-400 mt-3 text-sm">
          Submissions are reviewed by a moderator before they go public.
        </p>
      </div>

      <form
        onSubmit={handleSubmit}
        className="bg-ink-800/60 border border-ink-700 rounded-lg p-6 space-y-5"
      >
        <div>
          <label className="block text-xs uppercase tracking-wide text-ink-300 mb-1">
            Title
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={140}
            placeholder="Give it a name…"
            className="w-full bg-ink-900 border border-ink-600 focus:border-accent-500 focus:outline-none rounded-md px-3 py-2 text-ink-100 placeholder:text-ink-500"
          />
          <div className="flex justify-between text-xs text-ink-500 mt-1">
            <span>{titleOk ? "" : "At least 3 characters."}</span>
            <span>{title.length}/140</span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs uppercase tracking-wide text-ink-300 mb-1">
              Author display name
            </label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder={
                user?.displayName || user?.email?.split("@")[0] || "anonymous"
              }
              className="w-full bg-ink-900 border border-ink-600 focus:border-accent-500 focus:outline-none rounded-md px-3 py-2 text-ink-100 placeholder:text-ink-500"
            />
            <p className="text-xs text-ink-500 mt-1">
              Optional. Defaults to your account name.
            </p>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wide text-ink-300 mb-1">
              Tags
            </label>
            <input
              type="text"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="paranormal, hospital, dreams"
              className="w-full bg-ink-900 border border-ink-600 focus:border-accent-500 focus:outline-none rounded-md px-3 py-2 text-ink-100 placeholder:text-ink-500"
            />
            <p className="text-xs text-ink-500 mt-1">
              Comma-separated. Up to 8.
            </p>
          </div>
        </div>

        <div>
          <label className="block text-xs uppercase tracking-wide text-ink-300 mb-1">
            Story
          </label>
          <ReactQuill
            theme="snow"
            value={body}
            onChange={setBody}
            placeholder="Once, on a quiet evening…"
          />
          <div className="flex justify-between text-xs text-ink-500 mt-2">
            <span>
              {bodyOk ? "" : "Tell us at least a little — minimum ~50 characters."}
            </span>
            <span>{wordCount} words</span>
          </div>
        </div>

        {error && (
          <div className="text-sm text-red-300 bg-red-900/30 border border-red-800/50 rounded-md px-3 py-2">
            {error}
          </div>
        )}

        <div className="flex items-center justify-between pt-2">
          <p className="text-xs text-ink-500 max-w-md">
            By submitting, you confirm this story is yours to share.
          </p>
          <button
            type="submit"
            disabled={!canSubmit}
            className="px-5 py-2.5 rounded-md bg-accent-500 hover:bg-accent-400 disabled:opacity-50 disabled:cursor-not-allowed text-white tracking-wide"
          >
            {submitting ? "Submitting…" : "Submit story"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SubmitScreen;
